import classNames from 'classnames';
import { forwardRef, memo, useContext, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Loader } from '../../components/Loader';
import { moderationMessages } from '../../i18n/moderation';
import { useAppSelector } from '../../redux/hooks';
import { handleError } from '../../utils/common-utils';
import { ThreadInitProvider } from '../ThreadContexts/ThreadInitContext';
import { messageContext } from './messageContext';
import { ThreadFlattenMessage } from './ThreadFlattenMessage';
import type { ThreadMessageChildrenInstance } from './ThreadMessageNode';
import { ThreadMessageNode } from './ThreadMessageNode';

const messages = defineMessages({
  pinnedByEditors: { defaultMessage: 'Epinglé par la rédaction', id: 'iE1KxH' },
});

interface ThreadMessageRootProps {
  messageId: string;
  pathIds?: Array<string>;
  isNestedAnswer?: boolean;
  targetedId?: string;
  unlimited?: boolean;
  isBestAnswer?: boolean;
  moreBorders?: 'top' | 'bottom';
  isYakliDisplay?: boolean;
}

export const ThreadMessageRoot = memo(
  forwardRef<ThreadMessageChildrenInstance, ThreadMessageRootProps>(function ThreadMessageRoot(
    props,
    ref,
  ) {
    const { messageId, ...others } = props;
    const { moreBorders } = others;

    const reduxSliceToHandle = props.isYakliDisplay ? 'yakli' : 'threads';
    const hasAllMessages = useAppSelector(state => !!state[reduxSliceToHandle].messages);
    const messageIsDefined = useAppSelector(
      state => !!state[reduxSliceToHandle].messages?.[messageId],
    );
    const messageIsPinned = useAppSelector(
      state => state[reduxSliceToHandle].messages?.[messageId]?.pinned,
    );
    const messageIsPending = useAppSelector(
      state => state[reduxSliceToHandle].messages?.[messageId]?.status === 'pending',
    );
    const { mode } = useContext(messageContext);
    const isNestedMode = mode === 'nested' || props.isYakliDisplay;

    const [init, setInit] = useState(false);
    useEffect(() => {
      setInit(true);
    }, []);

    if (!hasAllMessages) {
      return (
        <div className="flex justify-center">
          <Loader black />
        </div>
      );
    }

    if (!messageIsDefined) {
      handleError(
        `ThreadMessageRoot found no corresponding Message in redux state for message ID ${messageId}`,
      );
      return null;
    }

    return (
      <ThreadInitProvider initialized={init}>
        <div
          className={classNames(
            // Padding-bottom: Used to be applied as a hack to avoid having the bottom border hidden by the content.
            // I didn't know exactly why it was required, it seemed to be a consequence of the auto-height component, which content's bg-white covered the border.
            // It doesn't seem to be required anymore, but just in case I'm wrong, let's keep this commented for a couple of weeks until we are sure it is stable, then remove this commented code and this comment.
            //  pb-[1px]
            'ThreadMessageRoot relative',
            isNestedMode && 'rounded border border-solid border-gray-210 bg-white',
          )}
        >
          {moreBorders && (
            <>
              <div
                className={classNames(
                  'absolute h-full left-1 right-1 -z-10 rounded border border-solid border-gray-semiLight',
                  moreBorders === 'top' ? 'bottom-1' : 'top-1',
                )}
              />
              <div
                className={classNames(
                  'absolute h-full left-2 right-2 -z-20 rounded border border-solid border-gray-light',
                  moreBorders === 'top' ? 'bottom-2' : 'top-2',
                )}
              />
            </>
          )}
          {isNestedMode ? (
            <ThreadMessageNode
              isRoot
              key={messageId}
              ref={ref}
              rootMessageId={messageId}
              parentMessageId={undefined}
              currentMessageId={messageId}
              {...others}
            />
          ) : (
            <ThreadFlattenMessage {...others} messageId={messageId} />
          )}

          <div
            className="absolute top-0 right-0 pr-5 flex space-x-2"
            style={{ transform: 'translateY(-50%)' }}
          >
            {messageIsPinned && (
              <div className="bg-primary-base text-white prose-caption p-2 py-1 rounded-lg">
                <i className="icon-pin mr-1" />
                <FormattedMessage {...messages.pinnedByEditors} />
              </div>
            )}

            {/* The message can be pending for various reasons. E.g. if the BO enabled pre-moderation, then regardless of the reason why the message is hidden, it is marked as pending (new post, ban...). The API manages this status. */}
            {messageIsPending && (
              <div className="bg-gray-medium text-white prose-caption p-2 py-1 rounded-lg">
                <i className="icon-clock mr-1" />
                <FormattedMessage {...moderationMessages.message.isPending} />
              </div>
            )}
          </div>
        </div>
      </ThreadInitProvider>
    );
  }),
);
